import type { Appointment, ScheduleBlock } from './types';
import { clinicDateKey } from './formatters';
import { effectiveAppointmentLifecycleStatus } from './schedule';

export interface DueAppointmentGroups {
  overdue: Appointment[];
  today: Appointment[];
  upcoming: Appointment[];
  unscheduled: Appointment[];
}

function overlaps(startAt: string, endAt: string, rangeStartAt: string, rangeEndAt: string) {
  return startAt < rangeEndAt && endAt > rangeStartAt;
}

export function mergeCanonicalScheduleRange(
  currentAppointments: Appointment[],
  currentBlocks: ScheduleBlock[],
  canonicalAppointments: Appointment[],
  canonicalBlocks: ScheduleBlock[],
  rangeStartAt: string,
  rangeEndAt: string,
): { appointments: Appointment[]; scheduleBlocks: ScheduleBlock[] } {
  const appointments = [
    ...currentAppointments.filter(
      (appointment) => !overlaps(appointment.startAt, appointment.endAt, rangeStartAt, rangeEndAt),
    ),
    ...canonicalAppointments,
  ].sort((left, right) => left.startAt.localeCompare(right.startAt));
  const scheduleBlocks = [
    ...currentBlocks.filter(
      (block) => !overlaps(block.startAt, block.endAt, rangeStartAt, rangeEndAt),
    ),
    ...canonicalBlocks,
  ].sort((left, right) => left.startAt.localeCompare(right.startAt));
  return { appointments, scheduleBlocks };
}

export function categorizeExpectedPayments(
  appointments: Appointment[],
  referenceAt: Date,
): DueAppointmentGroups {
  const referenceDate = clinicDateKey(referenceAt);
  return appointments.reduce<DueAppointmentGroups>(
    (groups, appointment) => {
      if (!appointment.expectedPaymentAt) {
        groups.unscheduled.push(appointment);
        return groups;
      }
      const expectedDate = clinicDateKey(appointment.expectedPaymentAt);
      if (expectedDate < referenceDate) groups.overdue.push(appointment);
      else if (expectedDate === referenceDate) groups.today.push(appointment);
      else groups.upcoming.push(appointment);
      return groups;
    },
    { overdue: [], today: [], upcoming: [], unscheduled: [] },
  );
}

export function isOutstandingCompletedAppointment(
  appointment: Pick<Appointment, 'balanceCents' | 'endAt' | 'lifecycleStatus'>,
  referenceAt: string | Date = new Date(),
): boolean {
  return (
    appointment.balanceCents > 0 &&
    effectiveAppointmentLifecycleStatus(appointment, referenceAt) === 'completed'
  );
}

export function appointmentAmountDueCents(
  appointment: Pick<Appointment, 'balanceCents' | 'lifecycleStatus'>,
): number {
  return ['cancelled', 'no_show', 'rescheduled'].includes(appointment.lifecycleStatus)
    ? 0
    : appointment.balanceCents;
}
